const DB_NAME = 'dsg-badge';
const DB_VERSION = 1;
const STORE = 'kv';
const KEY = 'count';

export function isBadgingSupported() {
    return typeof navigator !== 'undefined' && 'setAppBadge' in navigator;
}

function openDb() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet() {
    try {
        const db = await openDb();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const req = tx.objectStore(STORE).get(KEY);
            req.onsuccess = () => {
                const v = parseInt(req.result, 10);
                resolve(Number.isFinite(v) && v > 0 ? v : 0);
            };
            req.onerror = () => reject(req.error);
        });
    } catch (e) {
        return 0;
    }
}

async function idbSet(value) {
    try {
        const db = await openDb();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).put(value, KEY);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch (e) {
        // ignore write failures
    }
    return value;
}

async function applyBadge(value) {
    if (!isBadgingSupported()) return;
    try {
        if (value > 0) {
            await navigator.setAppBadge(value);
        } else if ('clearAppBadge' in navigator) {
            await navigator.clearAppBadge();
        }
    } catch (e) {
        console.warn('App badge update failed', e);
    }
}

export async function getBadgeCount() {
    return idbGet();
}

// Set the badge to an exact count. 0 (or omitted) clears it.
export async function setBadge(count = 0) {
    const value = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    await idbSet(value);
    await applyBadge(value);
    return value;
}

// Increment the badge by n (default 1) and return the new count.
export async function incrementBadge(n = 1) {
    const next = (await idbGet()) + n;
    return setBadge(next);
}

// Clear the badge (e.g. when the user opens/focuses the app).
export async function clearBadge() {
    return setBadge(0);
}

// Restore the persisted badge on app start (used when launched in the background).
export async function restoreBadge() {
    const count = await idbGet();
    if (count > 0) await applyBadge(count);
    return count;
}
